// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Wordloom — bootstrap and the screen machine. Owns which screen is up, the room's Net
// (created ONCE per session, never left for a rematch), and the glue between the pure
// game/Race and the DOM. Rules live in game.ts/score.ts; the board in render.ts.

import '@ben-gy/game-engine/mobile.css';
import './styles/main.css';

import { hardenViewport } from '@ben-gy/game-engine/mobile';
import { createSfx } from '@ben-gy/game-engine/sound';
import { createStore } from '@ben-gy/game-engine/storage';
import { newSeed } from '@ben-gy/game-engine/rng';
import { createNet, roomAppId, setTurnConfig, type Net } from '@ben-gy/game-engine/net';
import { getTurnConfig } from '@ben-gy/game-engine/turn';
import { createRounds, type RoundInfo, type Rounds, type RoundPlayer } from '@ben-gy/game-engine/rematch';
import {
  clearRoomInUrl,
  createLobby,
  createRoomEntry,
  normalizeRoomCode,
  setRoomInUrl,
} from '@ben-gy/game-engine/lobby';
import { resolveName } from '@ben-gy/game-engine/identity';

import { escapeHtml } from './dom';
import { MODE_IDS, modeOf, type Mode } from './modes';
import { createGame, current, place as applyPlace, score as scoreOf, tickTimer, type GameState } from './game';
import { scoreGrid, type GridScore } from './score';
import { tileValue } from './dict';
import { dailySeed, todayUTC } from './seed';
import { createBoard, type Board } from './render';
import { Race, seatOf, createOneShot, type RaceMsg } from './race';
import { runCountdown, type Countdown } from './countdown';
import { aboutHtml, ATTRIB, helpHtml, menuHtml, multiResultsHtml, soloResultsHtml } from './ui';

const SLUG = 'wordloom';
const REVEAL_MS = 1100;

// ── shell ───────────────────────────────────────────────────────────────────

const app = document.querySelector<HTMLElement>('#app')!;
let content: HTMLElement;

const store = createStore(SLUG);
const sfx = createSfx(store.get<boolean>('muted', false) ?? false);
const reducedMotion =
  typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

let unlocked = false;
function unlock(): void {
  if (unlocked) return;
  unlocked = true;
  try {
    sfx.unlock();
  } catch {
    /* audio unlock is best-effort */
  }
}
window.addEventListener('pointerdown', unlock, { once: true });
window.addEventListener('keydown', unlock, { once: true });

// ── persistent session state ─────────────────────────────────────────────────

let playerName = resolveName(store, () => `Player ${Math.floor(Math.random() * 900 + 100)}`);
void playerName;
let myModeChoice = modeOf(store.get<string>('mode', '')).id;
let hostOptsMode = myModeChoice;

function bestFor(modeId: string): number {
  return store.get<number>(`best_${modeId}`, 0) ?? 0;
}
function setBest(modeId: string, v: number): void {
  if (v > bestFor(modeId)) store.set(`best_${modeId}`, v);
}

let net: Net | null = null;
let rounds: Rounds | null = null;
let race: Race | null = null;
let roster: RoundPlayer[] = [];
let tally: number[] = [];
let roomCode = '';

// ── screen lifecycle ─────────────────────────────────────────────────────────

const cleanups: Array<() => void> = [];
function onCleanup(fn: () => void): void {
  cleanups.push(fn);
}
function teardown(): void {
  for (const fn of cleanups.splice(0)) {
    try {
      fn();
    } catch {
      /* a failing teardown must never block the next screen */
    }
  }
}
function setPlaying(on: boolean): void {
  document.body.classList.toggle('playing', on);
}

function mountShell(): void {
  app.innerHTML = `
    <main class="main-content" id="content"></main>
    <footer class="site-footer">${ATTRIB}</footer>`;
  content = document.getElementById('content')!;
}

function sheet(html: string): void {
  const wrap = document.createElement('div');
  wrap.className = 'overlay';
  wrap.innerHTML = `<div class="sheet">${html}</div>`;
  document.body.appendChild(wrap);
  const close = (): void => wrap.remove();
  wrap.addEventListener('pointerdown', (e) => {
    if (e.target === wrap) close();
  });
  wrap.querySelectorAll('.sheet-close').forEach((b) => b.addEventListener('click', close));
}

function toast(msg: string): void {
  const t = document.createElement('div');
  t.className = 'toast';
  t.textContent = msg;
  document.body.appendChild(t);
  setTimeout(() => t.classList.add('show'), 10);
  setTimeout(() => t.remove(), 1900);
}

// ── menu ─────────────────────────────────────────────────────────────────────

function showMenu(): void {
  teardown();
  setPlaying(false);
  content.innerHTML = menuHtml({
    modeId: myModeChoice,
    best: bestFor(myModeChoice),
    muted: sfx.muted(),
    dateStr: todayUTC(),
  });

  content.querySelectorAll<HTMLElement>('.chip').forEach((c) =>
    c.addEventListener('click', () => {
      myModeChoice = c.dataset.mode!;
      store.set('mode', myModeChoice);
      showMenu();
    }),
  );
  content.querySelector('#playDaily')!.addEventListener('click', () => startSolo(true));
  content.querySelector('#playRandom')!.addEventListener('click', () => startSolo(false));
  content.querySelector('#playFriends')!.addEventListener('click', showRoomEntry);
  content.querySelector('#help')!.addEventListener('click', () => sheet(helpHtml()));
  content.querySelector('#about')!.addEventListener('click', () => sheet(aboutHtml()));
  content.querySelector('#mute')!.addEventListener('click', () => {
    sfx.setMuted(!sfx.muted());
    store.set('muted', sfx.muted());
    showMenu();
  });

  if (!store.get<boolean>('seenHelp', false)) {
    store.set('seenHelp', true);
    sheet(helpHtml());
  }
}

// ── the game screen (shared by solo + multiplayer) ────────────────────────────

interface GameConfig {
  mode: Mode;
  timed: boolean;
  state: () => GameState;
  msLeft: () => number | null;
  place: (cellIdx: number, queueIdx: number) => boolean;
  tick: (dt: number) => void;
  onOver: () => void;
  onQuit: () => void;
}

interface GameScreen {
  destroy(): void;
}

function placeFeedback(letter: string, gained: boolean): void {
  try {
    const pitch = 0.85 + Math.min(1.4, tileValue(letter) / 6);
    sfx.play(gained ? 'coin' : 'blip', { pitch });
  } catch {
    /* audio best-effort */
  }
  if (!reducedMotion && 'vibrate' in navigator) {
    try {
      navigator.vibrate(gained ? 14 : 6);
    } catch {
      /* haptics best-effort */
    }
  }
}

function buildGameScreen(cfg: GameConfig): GameScreen {
  const mode = cfg.mode;
  content.innerHTML = `
    <section class="screen game">
      <div class="hud">
        <span class="hud-mode">${escapeHtml(mode.name)}</span>
        <span class="hud-score" id="hudScore">0</span>
        <button class="linkbtn hud-quit" id="quit">Quit</button>
      </div>
      <div class="timer-wrap" id="timerWrap" hidden><span class="timer-bar" id="timerBar"></span></div>
      <div class="tray" id="tray" aria-label="Letters to place"></div>
      <div class="board-host" id="boardHost"></div>
    </section>`;

  const boardHost = content.querySelector<HTMLElement>('#boardHost')!;
  const hudScore = content.querySelector<HTMLElement>('#hudScore')!;
  const tray = content.querySelector<HTMLElement>('#tray')!;
  const timerWrap = content.querySelector<HTMLElement>('#timerWrap')!;
  const timerBar = content.querySelector<HTMLElement>('#timerBar')!;
  content.querySelector('#quit')!.addEventListener('click', cfg.onQuit);

  let selQ = 0;
  let lastPlaced: number | null = null;
  let preview: { cell: number; letter: string } | null = null;
  let destroyed = false;
  let over = false;
  let prevScore = 0;
  let revealTimer: ReturnType<typeof setTimeout> | null = null;

  const board: Board = createBoard(boardHost, (cellIdx) => onCell(cellIdx));

  function renderTray(): void {
    const q = current(cfg.state());
    if (selQ >= q.length) selQ = 0;
    tray.innerHTML = q
      .map(
        (ch, i) =>
          `<button class="letter${i === selQ ? ' active' : ''}" data-q="${i}" type="button" aria-pressed="${i === selQ}">${escapeHtml(ch)}<span class="lv">${tileValue(ch)}</span></button>`,
      )
      .join('');
    tray.querySelectorAll<HTMLElement>('.letter').forEach((b) =>
      b.addEventListener('click', () => {
        selQ = Number(b.dataset.q);
        renderTray();
        renderBoard(false);
      }),
    );
    tray.classList.toggle('single', q.length <= 1);
  }

  function renderBoard(finalReveal: boolean): void {
    const st = cfg.state();
    const gs: GridScore = scoreGrid(st.cells, mode);
    hudScore.textContent = String(gs.total);
    board.render({
      cells: st.cells,
      size: mode.size,
      score: gs,
      lastPlaced,
      preview,
      reducedMotion,
      finalReveal,
    });
  }

  function updateTimer(): void {
    const ms = cfg.timed ? cfg.msLeft() : null;
    if (ms === null) {
      timerWrap.hidden = true;
      return;
    }
    timerWrap.hidden = false;
    const frac = Math.max(0, Math.min(1, ms / mode.roundMs));
    timerBar.style.width = `${(frac * 100).toFixed(1)}%`;
    timerBar.classList.toggle('low', frac <= 0.25);
  }

  function onCell(cellIdx: number): void {
    if (over || destroyed) return;
    const st = cfg.state();
    if (st.over || st.cells[cellIdx] != null) return;
    const q = current(st);
    if (selQ >= q.length) selQ = 0;
    const letter = q[selQ];
    const before = scoreOf(st, mode).total;
    const changed = cfg.place(cellIdx, selQ);
    if (!changed) return;
    lastPlaced = cellIdx;
    preview = null;
    const after = scoreOf(cfg.state(), mode).total;
    placeFeedback(letter, after > before);
    if (after > before && !reducedMotion) {
      boardHost.classList.remove('gain');
      void boardHost.offsetWidth;
      boardHost.classList.add('gain');
    }
    prevScore = after;
    selQ = 0;
    renderTray();
    renderBoard(false);
    if (cfg.state().over) finish();
  }

  function finish(): void {
    if (over) return;
    over = true;
    try {
      sfx.play('win');
    } catch {
      /* best-effort */
    }
    renderBoard(true);
    revealTimer = setTimeout(() => {
      if (!destroyed) cfg.onOver();
    }, reducedMotion ? 200 : REVEAL_MS);
  }

  function onKey(e: KeyboardEvent): void {
    if (over) return;
    if (e.key >= '1' && e.key <= '9') {
      const i = Number(e.key) - 1;
      const q = current(cfg.state());
      if (i < q.length) {
        selQ = i;
        renderTray();
        renderBoard(false);
      }
    }
  }
  window.addEventListener('keydown', onKey);

  // Desktop hover preview of the staged letter.
  function onHover(e: Event): void {
    const t = (e.target as HTMLElement).closest<HTMLElement>('.cell');
    const st = cfg.state();
    if (over || !t) {
      if (preview) {
        preview = null;
        renderBoard(false);
      }
      return;
    }
    const i = Number(t.dataset.i);
    if (!Number.isFinite(i) || st.cells[i] != null) return;
    const q = current(st);
    preview = { cell: i, letter: q[Math.min(selQ, q.length - 1)] };
    renderBoard(false);
  }
  boardHost.addEventListener('pointermove', onHover);
  boardHost.addEventListener('pointerleave', () => {
    if (preview) {
      preview = null;
      renderBoard(false);
    }
  });

  const loop = cfg.timed
    ? setInterval(() => {
        if (over || destroyed) return;
        cfg.tick(250);
        updateTimer();
        if (cfg.state().over) finish();
      }, 250)
    : null;

  void prevScore;
  renderTray();
  renderBoard(false);
  updateTimer();

  return {
    destroy() {
      destroyed = true;
      if (revealTimer) clearTimeout(revealTimer);
      if (loop) clearInterval(loop);
      window.removeEventListener('keydown', onKey);
      boardHost.removeEventListener('pointermove', onHover);
      board.destroy();
    },
  };
}

// ── solo ─────────────────────────────────────────────────────────────────────

let soloState: GameState | null = null;
let soloDaily = false;
let soloScreen: GameScreen | null = null;

function startSolo(daily: boolean): void {
  teardown();
  setPlaying(true);
  const mode = modeOf(myModeChoice);
  soloDaily = daily;
  const seed = daily ? dailySeed(mode, todayUTC()) : newSeed();
  soloState = createGame(mode, seed, false);

  soloScreen = buildGameScreen({
    mode,
    timed: false,
    state: () => soloState!,
    msLeft: () => null,
    place: (cellIdx, queueIdx) => {
      const before = soloState!;
      const next = applyPlace(before, cellIdx, queueIdx);
      if (next === before) return false;
      soloState = next;
      return true;
    },
    tick: () => {},
    onOver: () => endSolo(daily),
    onQuit: () => showMenu(),
  });
  onCleanup(() => soloScreen?.destroy());
  runCountdown(document.body, sfx, () => {});
}

function endSolo(daily: boolean): void {
  const st = soloState!;
  const mode = modeOf(st.modeId);
  const total = scoreOf(st, mode).total;
  setBest(mode.id, total);
  void daily;
  showSoloResults();
}

function showSoloResults(): void {
  teardown();
  setPlaying(false);
  const st = soloState!;
  const mode = modeOf(st.modeId);
  content.innerHTML = `
    ${soloResultsHtml(st, mode, bestFor(mode.id), soloDaily)}
    <div class="menu-actions">
      <button class="btn primary" id="again">${soloDaily ? 'Play random' : 'Play again'}</button>
      <button class="btn" id="share">Share result</button>
      <button class="btn ghost" id="menu">Main menu</button>
      <button class="btn ghost" id="feedback">Report a problem</button>
    </div>`;
  content.querySelector('#again')!.addEventListener('click', () => startSolo(false));
  content.querySelector('#menu')!.addEventListener('click', showMenu);
  content.querySelector('#feedback')!.addEventListener('click', (e) =>
    window.feedback?.open({ returnFocusTo: e.currentTarget as HTMLElement }),
  );
  content.querySelector('#share')!.addEventListener('click', () => void shareSolo(st, mode));
}

function shareSolo(st: GameState, mode: Mode): Promise<void> {
  const total = scoreGrid(st.cells, mode).total;
  const head = soloDaily ? `Wordloom ${todayUTC()} · ${mode.name}` : `Wordloom · ${mode.name}`;
  return share(`${head} — I wove a ${total}.\nhttps://wordloom.benrichardson.dev`);
}

async function share(text: string): Promise<void> {
  try {
    if (navigator.share) {
      await navigator.share({ text });
      return;
    }
  } catch {
    /* cancelled — fall through to copy */
  }
  try {
    await navigator.clipboard.writeText(text);
    toast('Result copied');
  } catch {
    toast('Could not copy');
  }
}

// ── multiplayer ──────────────────────────────────────────────────────────────

let netSend: (m: RaceMsg) => void = () => {};
let stOff: () => void = () => {};
let lobby: { repaint(): void; destroy(): void } | null = null;
let lobbyMounted = false;
let gameScreen: GameScreen | null = null;

function repaintLobby(): void {
  if (lobbyMounted) lobby?.repaint();
}

function showRoomEntry(): void {
  teardown();
  setPlaying(false);
  content.innerHTML = '<section class="screen"><div id="entry"></div></section>';
  const host = content.querySelector<HTMLElement>('#entry')!;

  const deep = new URL(location.href).searchParams.get('room');
  if (deep) {
    const code = normalizeRoomCode(deep);
    clearRoomInUrl();
    if (code.length >= 3) {
      void enterRoom(code, false);
      return;
    }
  }

  const entry = createRoomEntry({
    container: host,
    onSubmit: (code, created) => void enterRoom(normalizeRoomCode(code), created),
    onCancel: showMenu,
    title: 'Play with friends',
    subtitle: 'Everyone weaves the same letters. Start a room, or type a friend’s code.',
  });
  onCleanup(() => entry.destroy());
}

async function enterRoom(code: string, created: boolean): Promise<void> {
  teardown();
  setPlaying(false);
  roomCode = code;
  setRoomInUrl(code);
  tally = [];
  race = null;

  content.innerHTML = `<section class="screen"><div class="lobby-searching"><span class="spinner"></span> Connecting…</div></section>`;

  try {
    setTurnConfig(await getTurnConfig());
  } catch {
    /* fail open — TURN is an upgrade, never a dependency */
  }

  const n = createNet(
    { appId: roomAppId(SLUG), roomId: code, claimHost: created },
    {
      onHostChange: (_id, isSelfHost) => {
        race?.setHost(isSelfHost);
        repaintLobby();
      },
      onPeers: () => repaintLobby(),
      onPeerLeave: (id) => {
        const seat = seatOf(roster, id);
        if (seat !== null) race?.peerLeft(seat);
        repaintLobby();
        refreshMultiResults();
      },
      onPeerJoin: (id) => {
        const seat = seatOf(roster, id);
        if (seat !== null) race?.peerReturned(seat);
      },
    },
  );
  net = n;

  const r = createRounds<{ mode: string }>({
    net: n,
    playerName,
    minPlayers: 2,
    roundOpts: () => ({ mode: myModeChoice }),
    onRound: (info) => startNetRound(info),
    onChange: (s) => {
      const ho = s.hostOpts as { mode?: string } | null;
      if (ho && typeof ho.mode === 'string') hostOptsMode = modeOf(ho.mode).id;
      repaintLobby();
    },
  });
  rounds = r;

  const sendStatus = n.channel<RaceMsg>('st', (data, from) => {
    if (!race) return;
    const seat = seatOf(roster, from);
    if (seat === null) return;
    race.receive(data, seat);
    refreshMultiResults();
  });
  netSend = (m) => sendStatus(m);
  stOff = () => sendStatus.off();

  showLobby();
}

function showLobby(): void {
  const n = net;
  const r = rounds;
  if (!n || !r) return;
  lobbyMounted = true;
  setPlaying(false);

  content.innerHTML = `<section class="screen"><div id="lobby-host"></div></section>`;
  const isHost = (): boolean => n.isHost() && n.hostSettled();

  lobby = createLobby({
    container: content.querySelector<HTMLElement>('#lobby-host')!,
    net: n,
    rounds: r,
    roomCode,
    minPlayers: 2,
    maxPlayers: 6,
    onCancel: () => void leaveRoom(),
    modeSlot: () => {
      if (isHost()) {
        return `<div class="lobby-modes" id="lm">${MODE_IDS.map(
          (id) =>
            `<button class="chip mode-btn${id === myModeChoice ? ' on' : ''}" data-mode="${id}" aria-pressed="${id === myModeChoice}">${escapeHtml(modeOf(id).name)}</button>`,
        ).join('')}<p class="mode-blurb">${escapeHtml(modeOf(myModeChoice).blurb)}</p></div>`;
      }
      return `<p class="host-pick">${n.hostSettled() ? `The host chose <strong>${escapeHtml(modeOf(hostOptsMode).name)}</strong>` : 'Waiting for the host…'}</p>`;
    },
    onModeMount: () => {
      if (!isHost()) return;
      content.querySelectorAll<HTMLElement>('#lm .mode-btn').forEach((b) =>
        b.addEventListener('click', () => {
          myModeChoice = b.dataset.mode!;
          store.set('mode', myModeChoice);
          lobby?.repaint();
        }),
      );
    },
  });
  onCleanup(() => {
    lobby?.destroy();
    lobby = null;
  });
}

function startNetRound(info: RoundInfo<{ mode: string }>): void {
  teardown();
  lobbyMounted = false;
  roster = info.players;
  if (tally.length !== roster.length) tally = Array(roster.length).fill(0);
  const mode = modeOf(info.opts?.mode);
  const selfSeat = info.seated ? seatOf(roster, net?.selfId ?? '') : null;
  const names = roster.map((p) => p.name);

  const rc = new Race({
    mode,
    seed: info.seed,
    selfSeat,
    names,
    isHost: info.isHost,
    timed: true,
    deps: {
      send: (m) => netSend(m),
      onChange: () => refreshMultiResults(),
      onSelfEnd: () => {
        rounds?.finish();
        setTimeout(() => showMultiResults(), 700);
      },
    },
  });
  race = rc;
  setPlaying(true);

  if (selfSeat === null) {
    showMultiResults();
  } else {
    gameScreen = buildGameScreen({
      mode,
      timed: true,
      state: () => rc.state,
      msLeft: () => rc.msLeft,
      place: (cellIdx, queueIdx) => rc.place(cellIdx, queueIdx),
      tick: (dt) => rc.tick(dt),
      onOver: () => {
        /* onSelfEnd already scheduled the results */
      },
      onQuit: () => backToLobby(),
    });
    onCleanup(() => {
      gameScreen?.destroy();
      gameScreen = null;
    });
    const cd: Countdown = runCountdown(document.body, sfx, () => {});
    onCleanup(() => cd.cancel());
  }
}

// ── multiplayer results ──────────────────────────────────────────────────────

let resultsMounted = false;
let winsAwarded = false;
const closeWait = createOneShot(20_000, () => refreshMultiResults());

function showMultiResults(): void {
  teardown();
  setPlaying(false);
  resultsMounted = true;
  winsAwarded = false;
  content.innerHTML = `
    <section class="screen results" id="mres"></section>
    <div class="menu-actions" id="mact">
      <button class="btn primary" id="again">Play again</button>
      <p class="waiting-note" id="waiting" aria-live="polite"></p>
      <button class="btn" id="lobby">Back to lobby</button>
      <button class="btn ghost" id="menu">Main menu</button>
      <button class="btn ghost" id="feedback">Report a problem</button>
    </div>`;
  const again = content.querySelector<HTMLButtonElement>('#again')!;
  again.addEventListener('click', () => {
    rounds?.vote();
    again.disabled = true;
    again.textContent = 'Waiting for the others…';
  });
  content.querySelector('#lobby')!.addEventListener('click', () => backToLobby());
  content.querySelector('#menu')!.addEventListener('click', () => void leaveRoom());
  content.querySelector('#feedback')!.addEventListener('click', (e) =>
    window.feedback?.open({ returnFocusTo: e.currentTarget as HTMLElement }),
  );
  onCleanup(() => {
    resultsMounted = false;
    closeWait.cancel();
  });
  closeWait.poke();
  refreshMultiResults();
}

function refreshMultiResults(): void {
  if (!resultsMounted || !race) return;
  const rc = race;
  const box = content.querySelector<HTMLElement>('#mres');
  if (!box) return;

  if (!winsAwarded && rc.everyoneDone()) {
    winsAwarded = true;
    const top = rc.ranked().find((r) => !r.left);
    if (top && top.score > 0) {
      const best = rc.ranked().filter((r) => !r.left && r.score === top.score);
      if (best.length === 1) tally[top.seat] = (tally[top.seat] ?? 0) + 1;
    }
  }

  box.innerHTML = multiResultsHtml(rc.ranked(), rc.selfSeat, rc.mode, tally);

  const waiting = content.querySelector<HTMLElement>('#waiting');
  if (waiting) {
    const r = rounds?.state();
    if (r && r.startsInMs !== null) {
      waiting.textContent = `Next round in ${Math.ceil(r.startsInMs / 1000)}s — ${r.votes.length}/${Math.max(r.present.length, 1)} ready`;
    } else if (!rc.everyoneDone()) {
      const left = [...rc.results.values()].filter((x) => !x.over).map((x) => x.name);
      waiting.textContent = left.length ? `Still weaving: ${left.join(', ')}` : '';
    } else if (r && r.votes.length) {
      waiting.textContent = `${r.votes.length}/${Math.max(r.present.length, 1)} ready for another round`;
    } else {
      waiting.textContent = '';
    }
  }
}

function backToLobby(): void {
  teardown();
  race = null;
  if (net && rounds) showLobby();
  else showMenu();
}

async function leaveRoom(): Promise<void> {
  teardown();
  lobbyMounted = false;
  race = null;
  const n = net;
  const r = rounds;
  net = null;
  rounds = null;
  netSend = () => {};
  try {
    stOff();
  } catch {
    /* channel may already be gone */
  }
  stOff = () => {};
  r?.destroy();
  if (n) {
    try {
      await n.leave();
    } catch {
      /* leaving is best-effort */
    }
  }
  showMenu();
}

// ── boot ─────────────────────────────────────────────────────────────────────

function boot(): void {
  hardenViewport();
  mountShell();
  const room = new URL(location.href).searchParams.get('room');
  if (room && normalizeRoomCode(room).length >= 3) showRoomEntry();
  else showMenu();
  window.addEventListener('beforeunload', () => {
    void net?.leave();
  });
}

// A hook the browser-verification pass drives to play without synthetic pointer events
// (which do not always land on a DOM button). Test-only.
(window as unknown as { __wl?: unknown }).__wl = {
  placeSolo: (cellIdx: number, queueIdx = 0): GameState | null => {
    if (!soloState || soloState.over) return soloState;
    const next = applyPlace(soloState, cellIdx, queueIdx);
    soloState = next;
    return soloState;
  },
  autoSolo: (): GameState | null => {
    // Greedy fill: place the front letter in the empty cell that most raises the score.
    if (!soloState) return null;
    while (!soloState.over) {
      let bestCell = -1;
      let bestScore = -1;
      for (let i = 0; i < soloState.cells.length; i++) {
        if (soloState.cells[i] != null) continue;
        const cells = soloState.cells.slice();
        cells[i] = soloState.queue[0];
        const s = scoreGrid(cells, modeOf(soloState.modeId)).total;
        if (s > bestScore) {
          bestScore = s;
          bestCell = i;
        }
      }
      soloState = applyPlace(soloState, bestCell, 0);
    }
    return soloState;
  },
  tickSolo: (ms: number): GameState | null => {
    if (!soloState) return null;
    soloState = tickTimer(soloState, ms);
    return soloState;
  },
  state: (): GameState | null => soloState,
  score: (): number => (soloState ? scoreGrid(soloState.cells, modeOf(soloState.modeId)).total : 0),
};

boot();
